import { useEffect, useState } from 'react';

import lodash, { debounce } from 'lodash';
import { Controller } from 'react-hook-form';

import { Box, Card, Stack, TextField, useMediaQuery, useTheme } from '@mui/material';

import CompanyImage from '../CompanyImage';
import CompanySocialNetworkField from '../CompanySocialNetworkField';
import SocialIconsContainer from '../SocialIconsContainer';
import CompanyAutocomplete from './CompanyAutocomplete';
import { useCompanyForm } from './useCompanyForm';
import Spin from 'components/Spin';
import Text from 'components/Text';
import TitleFormComponent from 'components/TitleForm';
import { useUser } from 'global/UserContext';
import { useUserSocialNetworks } from 'hooks/useUserSocialNetworks';
import { useCompanySocialNetworks } from 'pages/CompanyData/hooks/useCompanySocialNetworks';
import 'react-phone-input-2/lib/style.css';
import { apiOrchestrator } from 'services/apiService';

import type { Company } from './useCompanyForm';

export const CompanyInfoForm = () => {
  const { user } = useUser();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    errors,
    selectedCompany,
    handleCompanySelect,
    handleClearSelection,
    onSubmit,
    loading,
  } = useCompanyForm();

  const {
    socialNetworks,
    loading: socialLoading,
    error: socialError,
    refreshSocialNetworks,
    useSocialNetworksUpdate,
  } = useCompanySocialNetworks(selectedCompany?.uuid || '');

  // Escuta atualizações nas redes sociais
  useSocialNetworksUpdate(() => {
    refreshSocialNetworks();
  });

  const { networkTypes } = useUserSocialNetworks();
  const [logo, setLogo] = useState<File | null>(null);

  const formValues = watch();
  const [isEdited, setIsEdited] = useState(false);

  const companyPosition = watch('companyPosition');

  const [openAdd, setOpenAdd] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState<string>('');

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Wrapper para controlar openAdd ao selecionar empresa
  const handleCompanySelectWrapper = (company: Company | null) => {
    handleCompanySelect(company);
    setOpenAdd(false);
  };

  // Wrapper para limpar seleção e openAdd
  const handleClearSelectionWrapper = () => {
    handleClearSelection();
    setOpenAdd(false);
  };

  useEffect(() => {
    if (selectedCompany) {
      setValue('name', selectedCompany.name);
      setValue('email', selectedCompany.email);
      setIsEdited(true);

      // Carrega o website existente
      const existingWebsite = socialNetworks.find(net => net.social_network_type === 'WEBSITE');
      setWebsiteUrl(existingWebsite?.social_network_username || '');
    }
  }, [selectedCompany, setValue, socialNetworks]);

  useEffect(() => {
    const updateCompanyRole = async () => {
      if (selectedCompany && companyPosition && isEdited) {
        try {
          await apiOrchestrator.put(
            `/users-person/update-company/${user?.id}?company_uuid=${selectedCompany.uuid}&company_role=${companyPosition}`
          );
        } catch (error) {
          console.error('Erro ao atualizar cargo:', error);
        }
      }
    };

    const debouncedUpdate = debounce(updateCompanyRole, 500);
    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [companyPosition, selectedCompany, user?.id, isEdited]);

  const handleFieldChange = () => {
    setIsEdited(true);
  };

  const debouncedSubmit = lodash.debounce(values => {
    if (isEdited) {
      handleSubmit(() => onSubmit(values))();
      setIsEdited(false);
    }
  }, 3000);

  useEffect(() => {
    if (isEdited) {
      debouncedSubmit(formValues);
    }

    return () => {
      debouncedSubmit.cancel();
    };
  }, [formValues, isEdited]);

  const handleLogoChange = async (file: File) => {
    if (!selectedCompany?.uuid) return;

    try {
      const formData = new FormData();
      formData.append('logo', file);

      await apiOrchestrator.post(`/companies/upload-logo/${selectedCompany.uuid}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setLogo(file);
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
    }
  };

  const debouncedWebsiteUpdate = lodash.debounce(async (url: string) => {
    if (!selectedCompany?.uuid) return;

    try {
      const existingNetwork = socialNetworks.find(net => net.social_network_type === 'WEBSITE');

      if (existingNetwork?.id) {
        // Atualiza website existente
        await apiOrchestrator.put(`/company-social-networks/${existingNetwork.id}`, {
          company_uuid: selectedCompany.uuid,
          social_network_type: 'WEBSITE',
          social_network_username: url,
        });
      } else {
        // Cria novo website
        await apiOrchestrator.post('/company-social-networks/', {
          company_uuid: selectedCompany.uuid,
          social_network_type: 'WEBSITE',
          social_network_username: url,
        });
      }

      refreshSocialNetworks();
    } catch (error) {
      console.error('Erro ao atualizar website:', error);
    }
  }, 2000);

  useEffect(() => {
    if (websiteUrl && selectedCompany) {
      const existingWebsite = socialNetworks.find(net => net.social_network_type === 'WEBSITE');
      if (websiteUrl !== (existingWebsite?.social_network_username || '')) {
        debouncedWebsiteUpdate(websiteUrl);
      }
    }

    return () => {
      debouncedWebsiteUpdate.cancel();
    };
  }, [websiteUrl, selectedCompany]);

  if (loading) {
    return <Spin centerHorizontally centerVertically />;
  }

  return (
    <Box>
      <TitleFormComponent title="Dados da Empresa" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={isMobile ? 1 : 2}>
          <Card sx={{ p: isMobile ? 1 : 2 }}>
            <CompanyAutocomplete
              onCompanySelect={handleCompanySelectWrapper}
              onClearSelection={handleClearSelectionWrapper}
              onAddNewCompany={() => setOpenAdd(v => !v)}
              selectedCompany={selectedCompany}
            />
          </Card>

          {(selectedCompany || openAdd) && (
            <>
              <Card sx={{ flex: 1, p: isMobile ? 1 : 2 }}>
                <Text color="textPrimary" bold>
                  Nome da empresa
                </Text>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      required
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      disabled={!!selectedCompany}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange();
                      }}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  )}
                />
              </Card>

              <Card sx={{ flex: 1, p: isMobile ? 1 : 2 }}>
                <Text color="textPrimary" bold>
                  Email da empresa
                </Text>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      required
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={!!selectedCompany}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange();
                      }}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  )}
                />
              </Card>

              {selectedCompany && (
                <Card sx={{ flex: 1, p: isMobile ? 1 : 2 }}>
                  <Text color="textPrimary" bold>
                    Website da empresa
                  </Text>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="https://www.exemplo.com.br"
                    value={websiteUrl}
                    onChange={e => setWebsiteUrl(e.target.value)}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Card>
              )}

              {selectedCompany && (
                <>
                  <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={isMobile ? 1 : 2}
                    width="100%"
                    alignItems={isMobile ? 'center' : 'stretch'}
                  >
                    <Box
                      width="100%"
                      height="100%"
                      sx={{
                        maxWidth: isMobile ? '100%' : '500px',
                      }}
                    >
                      <CompanyImage companyUuid={selectedCompany.uuid} />
                    </Box>

                    <Stack
                      direction="column"
                      spacing={isMobile ? 1 : 2}
                      width="100%"
                      sx={{
                        maxWidth: isMobile ? '100%' : 'calc(100% - 500px)',
                      }}
                    >
                      <Card sx={{ flex: 1, p: isMobile ? 1 : 2, borderRadius: 2 }}>
                        <Text color="textPrimary" bold>
                          Contatos
                        </Text>

                        <Box width="100%" height="100%" display="flex" justifyContent="center">
                          {socialNetworks.length > 0 ? (
                            <SocialIconsContainer
                              socialNetworks={socialNetworks}
                              socialLoading={socialLoading}
                              socialError={socialError}
                            />
                          ) : (
                            <Text color="textSecondary" textAlign="center">
                              Nenhuma rede social cadastrada para a empresa.
                            </Text>
                          )}
                        </Box>
                      </Card>

                      <Card sx={{ flex: 1, p: isMobile ? 1 : 2, borderRadius: 2, maxHeight: 100 }}>
                        <Text color="textPrimary" bold>
                          Cargo na empresa
                        </Text>
                        <Controller
                          name="companyPosition"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              required
                              fullWidth
                              size={isMobile ? 'small' : 'medium'}
                              onChange={e => {
                                field.onChange(e);
                                handleFieldChange();
                              }}
                            />
                          )}
                        />
                      </Card>
                    </Stack>
                  </Stack>

                  <CompanySocialNetworkField
                    networkTypes={networkTypes}
                    companyUuid={selectedCompany.uuid}
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </form>
    </Box>
  );
};

export default CompanyInfoForm;
